import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Bell,
  Calendar as CalendarIcon,
  Clock,
  Plus,
  Save,
  X,
  CheckCircle2,
  Trash2,
  MessageCircle,
  Edit3
} from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const formatDateBR = (s) => {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return s || '';
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y}`;
};

const todayStr = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const addMonthsToDate = (baseDate, months) => {
  const base =
    baseDate && /^\d{4}-\d{2}-\d{2}$/.test(baseDate)
      ? new Date(`${baseDate}T00:00:00`)
      : new Date();

  base.setMonth(base.getMonth() + months);

  const y = base.getFullYear();
  const m = String(base.getMonth() + 1).padStart(2, '0');
  const d = String(base.getDate()).padStart(2, '0');

  return `${y}-${m}-${d}`;
};

function RemindersPage() {
  const toast = useToast();
  const { user } = useAuth();
  const permissions = useMemo(() => new Set(user?.permissions || []), [user?.permissions]);
  const can = (permission) => permissions.has(permission);

  const [loading, setLoading] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [filter, setFilter] = useState('today'); // 'today' | '7d' | '30d' | 'all'

  // ----- Seleção opcional de paciente -----
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [selectedPatientName, setSelectedPatientName] = useState('');

  // ----- Formulário de novo lembrete -----
  const [isCreating, setIsCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDate, setNewDate] = useState(todayStr());
  const [newTime, setNewTime] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [editingReminder, setEditingReminder] = useState(null);

  const [sendingBirthdayIds, setSendingBirthdayIds] = useState({});

  // ---------------------------------------------------------------------------
  // Carrega lembretes (incluindo completados e aniversários)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchReminders = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        const { data } = await axios.get(`${API_URL}/reminders`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          // NÃO filtramos only_open, para que os "vistos" continuem aparecendo
          params: {
            include_birthdays: true,
          },
        });

        const sorted = [...(data || [])].sort((a, b) => {
          const ka = `${a.due_date || ''} ${a.due_time || ''}`;
          const kb = `${b.due_date || ''} ${b.due_time || ''}`;
          return ka.localeCompare(kb);
        });

        setReminders(sorted);
      } catch (e) {
        console.error('Erro ao carregar lembretes:', e);
        toast?.error?.('Erro ao carregar lembretes.');
      } finally {
        setLoading(false);
      }
    };

    fetchReminders();
  }, [toast]);

  // ---------------------------------------------------------------------------
  // Carrega lista de pacientes para vincular lembretes (opcional)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setPatients([]);
          return;
        }

        const headers = { Authorization: `Bearer ${token}` };
        const { data } = await axios.get(`${API_URL}/patients`, { headers });

        const listRaw = Array.isArray(data) ? data : data?.items || [];
        const list = [...listRaw].sort((a, b) =>
          (a.full_name || '').localeCompare(b.full_name || '', 'pt-BR', {
            sensitivity: 'base',
          })
        );

        setPatients(list);
      } catch (e) {
        console.error('Erro ao carregar pacientes para lembretes:', e);
        // Não travo a tela nem dou toast, porque lembrete sem paciente continua funcionando.
      }
    };

    fetchPatients();
  }, []);

  const filteredPatients = useMemo(() => {
    const q = patientSearch.trim().toLowerCase();
    const list = Array.isArray(patients) ? patients : [];
    const sorted = [...list].sort((a, b) =>
      (a.full_name || '').localeCompare(b.full_name || '', 'pt-BR', {
        sensitivity: 'base',
      })
    );
    if (!q) return sorted.slice(0, 20);

    return sorted
      .filter(
        (p) =>
          (p.full_name || '').toLowerCase().includes(q) ||
          (p.email || '').toLowerCase().includes(q) ||
          (p.phone || '').toLowerCase().includes(q)
      )
      .slice(0, 20);
  }, [patients, patientSearch]);

  // ---------------------------------------------------------------------------
  // Filtro por período
  // ---------------------------------------------------------------------------
  const filteredReminders = useMemo(() => {
    if (!Array.isArray(reminders)) return [];
    const baseDate = new Date(todayStr());

    return reminders.filter((r) => {
      if (!r.due_date) return false;
      const parts = r.due_date.split('-');
      if (parts.length !== 3) return false;
      const d = new Date(
        parseInt(parts[0], 10),
        parseInt(parts[1], 10) - 1,
        parseInt(parts[2], 10)
      );

      const diffMs = d.getTime() - baseDate.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (filter === 'today') {
        return diffDays === 0;
      }
      if (filter === '7d') {
        return diffDays >= 0 && diffDays <= 7;
      }
      if (filter === '30d') {
        return diffDays >= 0 && diffDays <= 30;
      }
      return true;
    });
  }, [reminders, filter]);

  const groupedByDate = useMemo(() => {
    const groups = {};
    for (const r of filteredReminders) {
      const key = r.due_date || 'Sem data';
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    }
    return Object.entries(groups).sort(([d1], [d2]) => d1.localeCompare(d2));
  }, [filteredReminders]);

  // ---------------------------------------------------------------------------
  // Criação de lembrete manual
  // ---------------------------------------------------------------------------
  const handleStartCreate = () => {
    setNewTitle('');
    setNewDate(todayStr());
    setNewTime('');
    setNewDescription('');

    setSelectedPatientId('');
    setSelectedPatientName('');
    setPatientSearch('');

    setIsCreating(true);
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewTitle('');
    setNewDate(todayStr());
    setNewTime('');
    setNewDescription('');
    setSelectedPatientId('');
    setSelectedPatientName('');
    setPatientSearch('');
  };

  const handleSaveCreate = async () => {
    if (!newTitle.trim()) {
      toast?.error?.('Informe um título para o lembrete.');
      return;
    }
    if (!newDate) {
      toast?.error?.('Informe a data do lembrete.');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const payload = {
        title: newTitle.trim(),
        description: newDescription.trim() || undefined,
        due_date: newDate,
        due_time: newTime || undefined,
        type: 'manual',
        origin: 'manual',
        patient_id: selectedPatientId || undefined,
        patient_name: selectedPatientName || undefined,
      };

      const { data } = await axios.post(`${API_URL}/reminders`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      setReminders((prev) => {
        const next = [...(prev || []), data];
        return next.sort((a, b) => {
          const ka = `${a.due_date || ''} ${a.due_time || ''}`;
          const kb = `${b.due_date || ''} ${b.due_time || ''}`;
          return ka.localeCompare(kb);
        });
      });

      toast?.success?.('Lembrete criado com sucesso.');
      handleCancelCreate();
    } catch (e) {
      console.error('Erro ao criar lembrete:', e);
      toast?.error?.('Erro ao criar lembrete.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Marcar como visto (NÃO remove da lista, só marca completed=true)
  // ---------------------------------------------------------------------------
  const handleMarkSeen = async (reminder) => {
    if (!reminder || reminder.type === 'birthday') {
      // Não marcamos aniversários como vistos no backend
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const { data } = await axios.patch(
        `${API_URL}/reminders/${reminder.id}/complete`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Atualiza o item na lista, sem remover
      setReminders((prev) =>
        (prev || []).map((r) => (r.id === data.id ? { ...r, ...data } : r))
      );

      toast?.success?.('Lembrete marcado como visto.');
    } catch (e) {
      console.error('Erro ao marcar lembrete como visto:', e);
      toast?.error?.('Erro ao marcar lembrete como visto.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendBirthdayWhatsApp = async (reminder) => {
    if (!reminder?.id || reminder.type !== 'birthday') return;

    const ok = window.confirm(
      `Enviar mensagem de aniversário por WhatsApp para ${reminder.patient_name || 'este paciente'}?`
    );

    if (!ok) return;

    try {
      setSendingBirthdayIds((prev) => ({ ...prev, [reminder.id]: true }));

      const token = localStorage.getItem('token');
      if (!token) return;

      await axios.post(
        `${API_URL}/reminders/${reminder.id}/send-birthday-whatsapp`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setReminders((prev) =>
        (prev || []).map((r) =>
          r.id === reminder.id ? { ...r, birthday_whatsapp_sent: true } : r
        )
      );

      toast?.success?.('Mensagem de aniversário enviada por WhatsApp.');
    } catch (e) {
      console.error('Erro ao enviar WhatsApp de aniversário:', e);
      toast?.error?.(
        e.response?.data?.detail || 'Erro ao enviar mensagem de aniversário.'
      );
    } finally {
      setSendingBirthdayIds((prev) => ({ ...prev, [reminder.id]: false }));
    }
  };

  // ---------------------------------------------------------------------------
  // Editar
  // ---------------------------------------------------------------------------  
  const handleStartEdit = (reminder) => {
    if (!reminder || reminder.type === 'birthday' || reminder.type === 'partner_activity') return;

    setEditingReminder({
      ...reminder,
      title: reminder.title || '',
      description: reminder.description || '',
      due_date: reminder.due_date || todayStr(),
      due_time: reminder.due_time || '',
      patient_id: reminder.patient_id || '',
      patient_name: reminder.patient_name || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingReminder?.id) return;

    if (!editingReminder.title.trim()) {
      toast?.error?.('Informe um título para o lembrete.');
      return;
    }

    if (!editingReminder.due_date) {
      toast?.error?.('Informe a data do lembrete.');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const payload = {
        title: editingReminder.title.trim(),
        description: editingReminder.description?.trim() || undefined,
        due_date: editingReminder.due_date,
        due_time: editingReminder.due_time || undefined,
        type: editingReminder.type || 'manual',
        origin: editingReminder.origin || 'manual',
        patient_id: editingReminder.patient_id || undefined,
        patient_name: editingReminder.patient_name || undefined,
      };

      const { data } = await axios.put(
        `${API_URL}/reminders/${editingReminder.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setReminders((prev) =>
        (prev || [])
          .map((r) => (r.id === data.id ? { ...r, ...data } : r))
          .sort((a, b) => {
            const ka = `${a.due_date || ''} ${a.due_time || ''}`;
            const kb = `${b.due_date || ''} ${b.due_time || ''}`;
            return ka.localeCompare(kb);
          })
      );

      setEditingReminder(null);
      toast?.success?.('Lembrete atualizado com sucesso.');
    } catch (e) {
      console.error('Erro ao atualizar lembrete:', e);
      toast?.error?.('Erro ao atualizar lembrete.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Excluir lembrete (permanece igual)
  // ---------------------------------------------------------------------------
  const handleDelete = async (reminder) => {
    const ok = window.confirm('Deseja realmente excluir este lembrete?');
    if (!ok) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      await axios.delete(`${API_URL}/reminders/${reminder.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setReminders((prev) =>
        (prev || []).filter((r) => r.id !== reminder.id)
      );
      toast?.success?.('Lembrete excluído.');
    } catch (e) {
      console.error('Erro ao excluir lembrete:', e);
      toast?.error?.('Erro ao excluir lembrete.');
    } finally {
      setLoading(false);
    }
  };

  const isMobileLike =
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    window.matchMedia('(max-width: 768px)').matches;

  const openWhatsAppWeb = async () => {
    const patientId = selectedPatientId || '';
    const patientName = selectedPatientName || '';

    if (isMobileLike) {
      toast?.info?.('No celular, use esse atalho somente quando houver telefone do paciente.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const params = new URLSearchParams();
      params.set('source', 'lembretes');
      if (patientId) params.set('patient_id', patientId);
      if (patientName) params.set('patient_name', patientName);

      const response = await fetch(`${API_URL}/extension/session-ticket?${params.toString()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (!response.ok || !data?.ticket) {
        console.error('Erro ao gerar ticket da extensão:', data);
        return;
      }

      const url = new URL('https://web.whatsapp.com');
      url.searchParams.set('odontoassist_source', 'lembretes');
      if (patientId) url.searchParams.set('patientId', patientId);
      if (patientName) url.searchParams.set('patientName', patientName);
      url.searchParams.set('ext_ticket', data.ticket);

      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Erro ao abrir integração com WhatsApp:', err);
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-6 gap-2 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Bell className="w-6 h-6 text-purple-600" />
              Lembretes
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Organize retornos, lembretes importantes e acompanhamentos dos pacientes.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {can('whatsapp.send') && <button
              type="button"
              onClick={openWhatsAppWeb}
              className="inline-flex items-center px-3 py-2 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-60"
              disabled={loading}
            >
              <MessageCircle className="w-4 h-4 mr-1" />
              Abrir WhatsApp Web
            </button>}

            {can('appointments.create') && <button
              type="button"
              onClick={handleStartCreate}
              className="inline-flex items-center px-3 py-2 rounded-lg bg-purple-600 text-white text-sm hover:bg-purple-700 disabled:opacity-60"
              disabled={loading}
            >
              <Plus className="w-4 h-4 mr-1" />
              Novo lembrete
            </button>}
          </div>
        </div>

        {/* Filtros rápidos */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { id: 'today', label: 'Hoje' },
            { id: '7d', label: 'Próximos 7 dias' },
            { id: '30d', label: 'Próximos 30 dias' },
            { id: 'all', label: 'Todos' },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                filter === f.id
                  ? 'bg-purple-100 border-purple-300 text-purple-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Formulário de novo lembrete */}
        {isCreating && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-purple-900 mb-3">
              Novo lembrete
            </h3>

            {/* Paciente opcional */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Paciente (opcional)
              </label>
              <div className="space-y-2">
                {selectedPatientId && (
                  <div className="flex items-center justify-between text-xs bg-white border border-purple-100 rounded-lg px-2 py-1.5">
                    <span className="text-gray-700 font-medium">
                      {selectedPatientName}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedPatientId('');
                        setSelectedPatientName('');
                      }}
                      className="text-[11px] text-purple-600 hover:underline"
                    >
                      Remover vínculo
                    </button>
                  </div>
                )}

                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Buscar paciente por nome, e-mail ou telefone..."
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                />

                {filteredPatients.length > 0 && (
                  <div className="max-h-40 overflow-y-auto bg-white border border-purple-100 rounded-lg text-xs">
                    {filteredPatients.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setSelectedPatientId(p.id);
                          setSelectedPatientName(p.full_name || '');
                          setPatientSearch('');
                        }}
                        className="w-full text-left px-3 py-1.5 hover:bg-purple-50 border-b last:border-b-0 border-purple-50"
                      >
                        <div className="font-medium text-gray-800">
                          {p.full_name}
                        </div>
                        <div className="text-[11px] text-gray-500">
                          {p.phone || p.email || ''}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-3 rounded-xl border border-purple-200 bg-white p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-purple-900">
                  Data rápida:
                </span>

                <button
                  type="button"
                  onClick={() => setNewDate(addMonthsToDate(todayStr(), 1))}
                  className="rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-700 hover:bg-purple-200"
                >
                  1 mês
                </button>

                <button
                  type="button"
                  onClick={() => setNewDate(addMonthsToDate(todayStr(), 3))}
                  className="rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-700 hover:bg-purple-200"
                >
                  3 meses
                </button>

                <button
                  type="button"
                  onClick={() => setNewDate(addMonthsToDate(todayStr(), 6))}
                  className="rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-700 hover:bg-purple-200"
                >
                  6 meses
                </button>

                <span className="text-xs text-gray-500">
                  ou escolha uma data no campo abaixo
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Título
                </label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Ex: Retorno para limpeza, Revisão canal 36..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Data
                </label>
                <input
                  type="date"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Horário (opcional)
                </label>
                <input
                  type="time"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Descrição (opcional)
              </label>
              <textarea
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                rows={3}
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Detalhes adicionais sobre o lembrete..."
              />
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={handleSaveCreate}
                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-purple-600 text-white text-sm hover:bg-purple-700 disabled:opacity-60"
                disabled={loading}
              >
                <Save className="w-4 h-4 mr-1" />
                {loading ? 'Salvando...' : 'Salvar lembrete'}
              </button>
              <button
                type="button"
                onClick={handleCancelCreate}
                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gray-200 text-gray-700 text-sm hover:bg-gray-300"
              >
                <X className="w-4 h-4 mr-1" />
                Cancelar
              </button>
            </div>
          </div>
        )}

        {editingReminder && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-blue-900 mb-3">
              Editar lembrete
            </h3>

            <div className="mb-3 rounded-xl border border-blue-200 bg-white p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-blue-900">
                  Data rápida:
                </span>

                <button
                  type="button"
                  onClick={() =>
                    setEditingReminder((prev) => ({
                      ...prev,
                      due_date: addMonthsToDate(todayStr(), 1),
                    }))
                  }
                  className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-200"
                >
                  1 mês
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setEditingReminder((prev) => ({
                      ...prev,
                      due_date: addMonthsToDate(todayStr(), 3),
                    }))
                  }
                  className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-200"
                >
                  3 meses
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setEditingReminder((prev) => ({
                      ...prev,
                      due_date: addMonthsToDate(todayStr(), 6),
                    }))
                  }
                  className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-200"
                >
                  6 meses
                </button>

                <span className="text-xs text-gray-500">
                  ou escolha uma data no campo abaixo
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Título
                </label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={editingReminder.title}
                  onChange={(e) =>
                    setEditingReminder((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Data
                </label>
                <input
                  type="date"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={editingReminder.due_date}
                  onChange={(e) =>
                    setEditingReminder((prev) => ({
                      ...prev,
                      due_date: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Horário (opcional)
                </label>
                <input
                  type="time"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={editingReminder.due_time || ''}
                  onChange={(e) =>
                    setEditingReminder((prev) => ({
                      ...prev,
                      due_time: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            {editingReminder.patient_name && (
              <div className="mb-3 text-xs bg-white border border-blue-100 rounded-lg px-2 py-1.5 text-gray-700">
                Paciente vinculado: <strong>{editingReminder.patient_name}</strong>
              </div>
            )}

            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Descrição (opcional)
              </label>
              <textarea
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                value={editingReminder.description || ''}
                onChange={(e) =>
                  setEditingReminder((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={handleSaveEdit}
                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                disabled={loading}
              >
                <Save className="w-4 h-4 mr-1" />
                {loading ? 'Salvando...' : 'Salvar alterações'}
              </button>

              <button
                type="button"
                onClick={() => setEditingReminder(null)}
                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gray-200 text-gray-700 text-sm hover:bg-gray-300"
              >
                <X className="w-4 h-4 mr-1" />
                Cancelar
              </button>
            </div>
          </div>
        )}        

        {/* Lista de lembretes */}
        {loading && reminders.length === 0 ? (
          <div className="text-sm text-gray-500">Carregando lembretes...</div>
        ) : groupedByDate.length > 0 ? (
          <div className="space-y-4">
            {groupedByDate.map(([dateKey, items]) => (
              <div key={dateKey} className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarIcon className="w-4 h-4 text-gray-500" />
                  <h4 className="text-sm font-semibold text-gray-800">
                    {formatDateBR(dateKey)}
                  </h4>
                </div>
                <div className="space-y-2">
                  {items.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-start justify-between border rounded-lg px-3 py-2 text-sm"
                    >
                      <div className="flex items-start gap-2">
                        <Bell className="w-4 h-4 text-purple-500 mt-1" />
                        <div>
                          <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                            <span>{r.title}</span>

                            {/* Badge de tipo / visto */}
                            {r.type === 'birthday' && (
                              <span className="px-2 py-0.5 rounded-full bg-pink-50 text-pink-700 text-[10px] font-semibold uppercase tracking-wide">
                                Aniversário
                              </span>
                            )}
                            {r.type === 'partner_activity' && (
                              <span className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-[10px] font-semibold uppercase tracking-wide">
                                Atividade comercial
                              </span>
                            )}
                            {r.type === 'compliance' && (
                              <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-semibold uppercase tracking-wide">
                                Boas Práticas
                              </span>
                            )}
                            {r.type && r.type !== 'birthday' && r.type !== 'partner_activity' && r.type !== 'compliance' && (
                              <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-[10px] font-semibold uppercase tracking-wide">
                                {r.type}
                              </span>
                            )}
                            {r.completed && (
                              <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold uppercase tracking-wide">
                                Visto
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
                            {r.due_time && (
                              <span className="inline-flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {r.due_time}
                              </span>
                            )}
                            {r.patient_name && (
                              <span className="inline-flex items-center gap-1">
                                Paciente:
                                <span className="font-medium text-gray-700">
                                  {r.patient_name}
                                </span>
                              </span>
                            )}
                          </div>
                          {r.description && (
                            <p className="text-xs text-gray-600 mt-1">
                              {r.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 ml-4">
                        {r.type === 'compliance' && r.action_url && (
                          <a href={r.action_url} className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs hover:bg-indigo-100">
                            {r.action_label || 'Abrir Boas Práticas'}
                          </a>
                        )}
                        {can('whatsapp.send') && r.type === 'birthday' && (
                          r.birthday_whatsapp_sent ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Mensagem enviada
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleSendBirthdayWhatsApp(r)}
                              className="inline-flex items-center px-2 py-1 rounded-full bg-green-50 text-green-700 text-xs hover:bg-green-100"
                              disabled={loading || sendingBirthdayIds[r.id]}
                            >
                              <MessageCircle className="w-3 h-3 mr-1" />
                              {sendingBirthdayIds[r.id] ? 'Enviando...' : 'Enviar WhatsApp'}
                            </button>
                          )
                        )}                      
                        {/* Botão Visto somente para lembretes normais (não aniversários) */}
                        {can('appointments.update') && r.type !== 'birthday' && r.type !== 'compliance' && (
                          <button
                            type="button"
                            onClick={() => !r.completed && handleMarkSeen(r)}
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                              r.completed
                                ? 'bg-emerald-50 text-emerald-500 cursor-default'
                                : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                            }`}
                            disabled={loading || r.completed}
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            {r.completed ? 'Visto' : 'Marcar como visto'}
                          </button>
                        )}
                        {/* Excluir não aparece para aniversários */}
                        {can('appointments.update') && r.type !== 'birthday' && r.type !== 'partner_activity' && r.type !== 'compliance' && (
                          <button
                            type="button"
                            onClick={() => handleDelete(r)}
                            className="inline-flex items-center px-2 py-1 rounded-full bg-red-50 text-red-600 text-xs hover:bg-red-100"
                            disabled={loading}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Excluir
                          </button>
                        )}
                      </div>

                      {can('appointments.update') && r.type !== 'birthday' && r.type !== 'partner_activity' && r.type !== 'compliance' && (
                        <button
                          type="button"
                          onClick={() => handleStartEdit(r)}
                          className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-blue-700 text-xs hover:bg-blue-100"
                          disabled={loading}
                        >
                          <Edit3 className="w-3 h-3 mr-1" />
                          Editar
                        </button>
                      )}

                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg border">
            <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-800 mb-1">
              Nenhum lembrete para este período
            </h3>
            <p className="text-sm text-gray-500 mb-3">
              Use o botão &quot;Novo lembrete&quot; para agendar retornos, aniversários
              e acompanhamentos.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default RemindersPage;
